import { createSlice } from '@reduxjs/toolkit'
import { ActionType } from 'typesafe-actions'
import { toDate } from 'date-fns'
import { invoiceApiEvents } from 'modules/api/invoice'
import { $TSFixMe } from 'types/ts-migrate'
import { reconApiEvents } from '../../api/recon'

export const initialState = {
  newInvoice: {
    modalOpen: false,
  },
  invoiceView: {
    editing: false,
  },
  nudgedInvoices: [],
  currentInvoice: null,
  /**
   * True from the moment "Send now" is clicked on a draft until the `saveAndSendInvoice` epic
   * settles. The epic saves the draft through `forkEpic`, so `invoiceUpdate_request` never
   * reaches the store and `apiState` reports nothing in flight for that leg — this flag is
   * what tells the invoice form it is mid-submit.
   */
  saveAndSendSubmitting: false,
  animations: {
    bulkDelete: [],
    bulkRestore: [],
    bulkSend: [],
    bulkSendErrors: [],
    bulkApprovePayments: [],
    bulkNudge: [],
  },
}

const slice = createSlice({
  name: 'invoiceUi',
  initialState,
  reducers: {
    toggleNewModal: (state, { payload }) => {
      state.newInvoice.modalOpen = !state.newInvoice.modalOpen
    },
    toggleEdit: (state, { payload }) => {
      state.invoiceView.editing = !state.invoiceView.editing
    },
    invoiceOpened: (state, { payload }) => {
      state.currentInvoice = payload.invoiceId
      // Belt-and-braces: never carry a stale submit guard into a freshly opened invoice
      state.saveAndSendSubmitting = false
    },
    closeInvoice: (state, { payload }) => {
      state.currentInvoice = null
    },
    // Draft Invoices
    bulkActionDelete: (state, { payload }) => {
      state.animations.bulkDelete = payload
    },
    bulkActionRestore: (state, { payload }) => {
      state.animations.bulkRestore = payload
    },
    bulkActionSend: (state, { payload }) => {
      state.animations.bulkSend = payload
    },
    // Open invoices
    bulkActionApprovePayments: (state, { payload }) => {
      state.animations.bulkApprovePayments = payload
    },
    bulkActionNudgeInvoices: (state, { payload }) => {
      state.animations.bulkNudge = payload
    },
    saveAndSendInvoice: (state, { payload }) => {
      state.saveAndSendSubmitting = true
    },
    /**
     * Emitted by the `saveAndSendInvoice` epic once the draft save has resolved, on both the
     * success and the failure path. On success `sendInvoices_request` is dispatched in the same
     * batch, so `apiState` takes over as the in-flight signal without a gap.
     */
    saveAndSendInvoiceSettled: state => {
      state.saveAndSendSubmitting = false
    },
  },
  extraReducers: {
    // @ts-expect-error ts-migrate(2464) FIXME: A computed property name must be of type 'string',... Remove this comment to see the full error message
    [reconApiEvents.invoicesNudge_success]: (state, { payload }) => {
      const ids: $TSFixMe = payload?.nudgeResult.map(({ id }: any) => id)
      ids.forEach((id: any) => {
        state.nudgedInvoices.push({ id: payload, lastNudged: toDate(Date.now()).getTime() })
      })
      state.animations.bulkNudge = state.animations.bulkNudge.filter((id: $TSFixMe) => !ids.includes(id))
    },
    // @ts-expect-error ts-migrate(2464) FIXME: A computed property name must be of type 'string',... Remove this comment to see the full error message
    [invoiceApiEvents.sendInvoices_success]: (state, { payload }) => {
      state.animations.bulkSendErrors = payload.filter(
        (r: $TSFixMe) => !r.sent && !r.result.includes('has already been sent'),
      )
    },
  },
})

/**
 * Exports
 */
const { reducer, actions: events } = slice

export type UiInvoiceEvent = ActionType<typeof events>

export { events, reducer }
